import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { usePatient } from "@/context/PatientContext";
import {
  Pill, Stethoscope, CalendarBlank, Storefront, MapPin, IdentificationCard,
  Bell, CheckSquare, Question, UsersThree, UserCircle, PencilSimple, Trash, Plus, Check
} from "@phosphor-icons/react";

const RESOURCE_ICON = {
  patient: UserCircle, doctors: Stethoscope, appointments: CalendarBlank,
  medications: Pill, pharmacies: Storefront, addresses: MapPin,
  insurance_cards: IdentificationCard, reminders: Bell, todos: CheckSquare,
  questions: Question, follow_ups: CalendarBlank, share: UsersThree,
};

const RESOURCE_LABEL = {
  patient: "patient", doctors: "doctor", appointments: "appointment",
  medications: "medication", pharmacies: "pharmacy", addresses: "address",
  insurance_cards: "insurance card", reminders: "reminder", todos: "to-do",
  questions: "question", follow_ups: "follow-up", share: "collaborator",
};

const ACTION_ICON = {
  created: Plus, updated: PencilSimple, deleted: Trash,
  completed: Check, reopened: PencilSimple, answered: Check,
  invited: UsersThree, joined: UsersThree,
};

const ACTION_VERB = {
  created: "added", updated: "updated", deleted: "removed",
  completed: "completed", reopened: "reopened", answered: "answered",
  invited: "invited", joined: "joined as collaborator",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TimelineFeed({ patientId, limit = 100, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/activities", { params: { patient_id: patientId, limit } });
      setItems(data);
    } finally { setLoading(false); }
  }, [patientId, limit]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-2">{[0,1,2].map((i) => <div key={i} className="h-14 rounded-xl bg-[#EBE8E0]/50 animate-pulse" />)}</div>
    );
  }
  if (items.length === 0) {
    return <div className="text-sm text-[#576A62] py-2">No activity yet. Once records are added, you'll see them here.</div>;
  }
  return (
    <div className="space-y-2" data-testid="timeline-feed">
      {items.map((a) => {
        const ResIcon = RESOURCE_ICON[a.resource] || PencilSimple;
        const ActIcon = ACTION_ICON[a.action] || PencilSimple;
        const verb = ACTION_VERB[a.action] || a.action;
        const resLabel = RESOURCE_LABEL[a.resource] || a.resource;
        return (
          <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-start gap-3 rounded-xl px-3 py-2 hover:bg-[#FDFBF7] border border-transparent hover:border-[#EBE8E0] transition-colors">
            <div className="relative shrink-0">
              <div className="w-9 h-9 rounded-full bg-[#EBE8E0] flex items-center justify-center">
                <ResIcon size={18} weight="duotone" className="text-[#2C5545]" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-[#EBE8E0] flex items-center justify-center">
                <ActIcon size={11} weight="bold" className="text-[#576A62]" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className={`text-sm leading-snug ${compact ? "" : "sm:text-[15px]"}`}>
                <span className="font-semibold">{a.user_name}</span>{" "}
                <span className="text-[#576A62]">{verb}</span>{" "}
                <span className="text-[#576A62]">{resLabel}</span>
                {a.resource_name && (
                  <> <span className="font-medium text-[#1A2520]">"{a.resource_name}"</span></>
                )}
                {a.details?.role && a.resource === "share" && (
                  <span className="text-xs text-[#576A62] ml-1">as {a.details.role}</span>
                )}
              </div>
              <div className="text-xs text-[#576A62] mt-0.5">{timeAgo(a.created_at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
